
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { createMemberIdSchema } from '../schemas/memberIdSchema.js';

// お知らせモーダル
export function NoticeModal({ open, message, onClose }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>お知らせ</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} autoFocus>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const ConfirmModal = ({
  open,
  message,
  onCancel,
  onConfirm,
  isMember = false,
  validationPattern = null,
  validationType = null,
  minDigits = null,
  maxDigits = null,
  memberIdLabel = null,
  defaultMemberId = null,
  isPostLinked = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    resolver: zodResolver(
      createMemberIdSchema(validationPattern, minDigits, maxDigits, validationType)
    ),
    mode: 'onChange',
    defaultValues: {
      memberId: '',
      memberIdConfirmation: '',
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
    } else if (defaultMemberId && !isPostLinked) {
      // 手入力時のみデフォルト値を設定（編集可能）
      reset({
        memberId: defaultMemberId,
        memberIdConfirmation: defaultMemberId,
      });
    }
  }, [open, reset, defaultMemberId, isPostLinked]);

  const handleFormSubmit = (data) => {
    onConfirm(data.memberId);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleConfirmClick = () => {
    if (isMember) {
      if (isPostLinked) {
        // POST連携時は defaultMemberId を直接使用（バリデーション不要）
        onConfirm(defaultMemberId);
      } else {
        // 手入力時はバリデーション後に送信
        handleSubmit(handleFormSubmit)();
      }
    } else {
      onConfirm();
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>確認</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: isMember ? 2 : 0 }}>
          {message}
        </DialogContentText>
        {isMember && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
              {memberIdLabel}
            </Typography>

            {isPostLinked ? (
              /* POST連携時：会員IDは編集不可、確認用フィールドは非表示 */
              <TextField
                value={defaultMemberId || ''}
                placeholder="会員ID"
                autoComplete="off"
                fullWidth
                margin="normal"
                disabled
              />
            ) : (
              /* 手入力時：会員ID + 確認用フィールドを表示 */
              <>
                <TextField
                  {...register('memberId')}
                  placeholder="会員ID"
                  autoComplete="off"
                  fullWidth
                  margin="normal"
                  error={!!errors.memberId}
                  helperText={errors.memberId?.message}
                  autoFocus
                />

                <TextField
                  {...register('memberIdConfirmation')}
                  placeholder="会員ID（確認用）"
                  autoComplete="off"
                  fullWidth
                  margin="normal"
                  error={!!errors.memberIdConfirmation}
                  helperText={errors.memberIdConfirmation?.message}
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} variant="outlined">
          いいえ
        </Button>
        <Button
          onClick={handleConfirmClick}
          variant="contained"
          color="primary"
          disabled={isMember && !isPostLinked && !isValid}
        >
          はい
        </Button>
      </DialogActions>
    </Dialog>
  );
};